import React from 'react';
import type { MigrationStep } from '../../utils/migrationSteps';
import { ui } from '../../config/uiSizes';

type StepStatus = 'completed' | 'approval' | 'active' | 'pending';

interface MigrationProgressStepperProps {
  steps: MigrationStep[];
}

const stepBg: Record<StepStatus, string> = {
  completed: 'bg-green-500 dark:bg-green-600',
  approval: 'bg-amber-500 dark:bg-amber-600',
  active: 'bg-blue-500 dark:bg-blue-600',
  pending: 'bg-gray-300 dark:bg-gray-600'
};

const stepText: Record<StepStatus, string> = {
  completed: 'text-green-700 dark:text-green-400',
  approval: 'text-amber-700 dark:text-amber-400',
  active: 'text-blue-700 dark:text-blue-400',
  pending: 'text-gray-400 dark:text-gray-300'
};

const StepIcon: React.FC<{ status: StepStatus; index: number }> = ({ status, index }) => {
  if (status === 'completed') {
    return (
      <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
        <path
          fillRule="evenodd"
          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
          clipRule="evenodd"
        />
      </svg>
    );
  }
  if (status === 'approval') {
    return <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse block" />;
  }
  if (status === 'active') {
    return <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse block" />;
  }
  return (
    <span className={`${ui.text.xxs} font-bold text-gray-400 dark:text-gray-400`}>{index + 1}</span>
  );
};

export const MigrationProgressStepper: React.FC<MigrationProgressStepperProps> = ({ steps }) => {
  const midpointStep = steps.find((s) => s.midpointLabel);

  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-5 py-3 mb-4">
      <div className="flex items-center justify-between mb-2.5">
        <span className={`${ui.text.xxs} font-bold uppercase tracking-widest text-gray-400 dark:text-gray-300`}>
          Migration Progress
        </span>
        {midpointStep?.midpointLabel && (
          <span className={`${ui.text.xxs} font-semibold px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-700`}>
            {midpointStep.midpointLabel}
          </span>
        )}
      </div>
      <div className="relative flex items-start pb-6">
        {steps.map((step, index) => {
          const prevStep = index > 0 ? steps[index - 1] : undefined;
          const connectorColor = prevStep?.status === 'completed'
            ? 'bg-green-400 dark:bg-green-600'
            : 'bg-gray-300 dark:bg-gray-600';
          const hasMidpoint = prevStep?.midpointLabel != null;
          return (
            <div key={step.label} className="flex-1 flex flex-col items-center relative">
              {/* Connector line */}
              {index > 0 && (
                <div
                  className={`absolute top-3 right-1/2 w-full h-0.5 z-0 ${connectorColor}`}
                />
              )}
              {/* Midpoint indicator — sits at the centre of the connector line */}
              {hasMidpoint && (
                <div className="absolute top-[2px] xl:top-[2px] lg:top-[-12px] md:top-[-12px] left-0 -translate-x-1/2 z-20 flex flex-col-reverse xl:flex-col items-center">
                  <div className={`${ui.svg.sm} rounded-full bg-amber-500 dark:bg-amber-600 ring-2 ring-offset-1 ring-amber-400 dark:ring-offset-gray-800 flex items-center justify-center`}>
                    <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse block" />
                  </div>
                  <span className={`mb-1 xl:mb-0 xl:mt-1 ${ui.text.xxs} font-bold text-amber-700 dark:text-amber-400 whitespace-nowrap bg-amber-50 dark:bg-amber-900/30 px-1 py-px rounded border border-amber-200 dark:border-amber-800`}>
                    {prevStep?.midpointLabel}
                  </span>
                </div>
              )}
              {/* Circle */}
              <div
                className={`relative z-10 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${stepBg[step.status]} ${step.status === 'active' ? 'ring-2 ring-offset-1 ring-blue-400 dark:ring-offset-gray-800' : ''} ${step.status === 'approval' ? 'ring-2 ring-offset-1 ring-amber-400 dark:ring-offset-gray-800' : ''}`}
              >
                <StepIcon status={step.status} index={index} />
              </div>
              {/* Label + sub-label */}
              <div className="mt-1 flex flex-wrap items-center justify-center gap-1">
                <span className={` ${ui.text.sm} font-semibold text-center ${stepText[step.status]}`}>
                  {step.label}
                </span>
                {step.subLabel && (
                  <span className={` ${ui.text.xxs} font-bold px-1.5 py-px rounded-full ${step.status === 'approval'
                      ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
                      : step.status === 'completed'
                        ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300'
                    }`}>
                    {step.subLabel}
                  </span>
                )}
              </div>
              {/* Description */}
              <span className={`mt-0.5 ${ui.text.xxs} text-gray-500 dark:text-gray-300 text-center leading-tight px-1 max-w-[120px] break-words`}>
                {step.description}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/** Pre-built step configs for each migration approval page */

